// Frontend taxonomy metadata for the reject-flow reason codes.
//
// The codes themselves are typed in `@/api/types` (extracted from the
// FastAPI Literal via the generated schema). This module ships display
// labels + one-line tooltips per code so every UI surface — the picker,
// the pills on the finding detail panel, the run-page filter — speaks
// the same vocabulary.
//
// The API canonicalizes retired codes on read (the INP-682 merge map in
// `server_py/engine/reason_codes.py`), so this module only ever sees
// the current taxonomy.
//
// Adding a code: bump the Pydantic Literal in
// `server_py/engine/reason_codes.py`, run `npm run gen:api`, then add
// the matching entry to `REASON_CODE_META` here.

import type { ReasonCode } from '@/api/types';

/** Stable display order — by raw frequency in the NVG corpus + the
 *  Andrew-refined groupings, new INP-682 codes at the tail. */
export const REASON_CODES_ORDERED: readonly ReasonCode[] = [
  'mixed_supply',
  'immaterial_amount',
  'non_resident_unregistered',
  'effective_eq_expected',
  'bank_fee_or_disbursement',
  'supplier_unregistered',
  'exempt_supply',
  'compliance_not_iri',
  'crown_corp',
  'retainer_or_prepayment',
  'pos_schedule_ix_error',
  'no_tax_charged',
  'recipient_name_mismatch',
  'no_invoice_available',
  'non_cad_transaction',
  'previously_claimed',
] as const;

export interface ReasonCodeMeta {
  /** Short label shown on the checkbox + pill — Title Case for legibility. */
  label: string;
  /** One-line tooltip that appears on hover; explains the rejection
   *  bucket in practitioner-facing language. */
  tooltip: string;
  /** Workflow hint from the taxonomy source (Andrew 2026-06-25):
   *  'reject' = the code alone is dispositive; 'needs_review' = the
   *  code flags a question to verify before disposition. Only set
   *  where the source specified it — the full dispositive/indicative
   *  mapping for the original 12 codes is pending Andrew's table. */
  dispositionHint?: 'reject' | 'needs_review';
}

export const REASON_CODE_META: Record<ReasonCode, ReasonCodeMeta> = {
  mixed_supply: {
    label: 'Mixed supply',
    tooltip:
      'Invoice bundles taxable + exempt components (non-taxable disbursements, pass-through lines). Engine can’t split them.',
  },
  immaterial_amount: {
    label: 'Immaterial amount',
    tooltip:
      'Total at-risk dollars too small to be worth filing. Long-tail residual.',
  },
  non_resident_unregistered: {
    label: 'Non-resident vendor',
    tooltip:
      'Non-resident vendor not registered under the regular regime — either a $0 dead end, or (simplified-registry vendors, ETA s.211.13) a vendor refund via s.232. No ITC either way.',
  },
  effective_eq_expected: {
    label: 'Effective ≈ expected',
    tooltip:
      'Effective tax rate matches the expected rate within rounding. Engine mis-fired on noise.',
  },
  bank_fee_or_disbursement: {
    label: 'Bank fee / disbursement',
    tooltip:
      'Bank fee or pass-through disbursement line on an otherwise correctly-taxed bill. Not a separate supply.',
  },
  supplier_unregistered: {
    label: 'Supplier unregistered',
    tooltip:
      'Canadian supplier under the $30K small-supplier threshold or registration lapsed. Verify via CRA registry.',
  },
  exempt_supply: {
    label: 'Exempt supply',
    tooltip:
      'Exempt under ETA Sch V — financial services, insurance premiums, share/asset-sale inputs. NOTE: M&A advisory bills are TAXABLE — apply to the sale supply itself, not the advisor’s bill.',
  },
  compliance_not_iri: {
    label: 'Compliance, not IRI',
    tooltip:
      'Wrong rate charged (5% GST on ON bill, etc.). Supplier owes a corrected invoice — compliance issue, not an InputRecovery claim.',
  },
  crown_corp: {
    label: 'Crown corporation',
    tooltip:
      'Federal Crown corp (EDC, BDC, etc.). ITC restrictions apply; verify against the agency’s registration.',
  },
  retainer_or_prepayment: {
    label: 'Retainer / prepayment',
    tooltip:
      'Bookkeeper-coded retainer or prepayment. ETA s.152(1) timing-of-claim question — verify when tax became payable before deciding.',
    dispositionHint: 'needs_review',
  },
  pos_schedule_ix_error: {
    label: 'Place-of-supply error',
    tooltip:
      'Supplier applied the wrong-province rate under Schedule IX (recipient’s province governs services). Supplier compliance issue, not a recovery.',
    dispositionHint: 'reject',
  },
  no_tax_charged: {
    label: 'No tax charged',
    tooltip:
      'The memo or the attached invoice confirms no HST was collected (“plus applicable tax…” language, a no-tax invoice). Nothing was paid, so nothing to recover.',
    dispositionHint: 'reject',
  },
  recipient_name_mismatch: {
    label: 'Wrong recipient (F6)',
    tooltip:
      'Invoice recipient name does not match the entity claiming the ITC (personal account, or a US/other-entity bill-to). Per SOR/91-45 s.3(c) F6 the recipient name is mandatory — the claimant isn’t the legal recipient.',
    dispositionHint: 'reject',
  },
  no_invoice_available: {
    label: 'No invoice available',
    tooltip:
      'The supporting invoice can’t be obtained from the client or supplier, so the SOR/91-45 documentation requirements for the claim can’t be met.',
    dispositionHint: 'reject',
  },
  non_cad_transaction: {
    label: 'Non-CAD transaction',
    tooltip:
      'Foreign-currency purchase confirmed to carry no GST/HST — the flagged amount is currency noise. Currency alone isn’t dispositive: a Canadian registrant can bill in USD and still charge HST, so verify the supplier first.',
    dispositionHint: 'needs_review',
  },
  previously_claimed: {
    label: 'Previously claimed',
    tooltip:
      'The ITC was already claimed on a filed GST/HST return — re-claiming would double-count. Matched against the filed-claim ledger.',
    dispositionHint: 'reject',
  },
};
